import * as Dialog from '@radix-ui/react-dialog';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { HelpId } from '../../help/topics';
import { InfoTip } from '../help/InfoTip';

type Variant = 'primary' | 'ghost' | 'danger' | 'subtle';

export function Button({
  variant = 'ghost',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  const styles: Record<Variant, string> = {
    primary: 'bg-amber text-ink-900 font-bold hover:bg-amber-2 disabled:opacity-50',
    ghost: 'border border-line-2 text-fg-2 hover:border-amber hover:text-amber disabled:opacity-40',
    danger: 'border border-hostile-line text-hostile-3 hover:bg-hostile-bg disabled:opacity-40',
    subtle: 'text-muted hover:text-amber disabled:opacity-40',
  };
  return (
    <button
      type="button"
      className={`inline-flex h-8 items-center justify-center gap-2 px-3 font-mono text-[11px] tracking-[0.08em] uppercase transition-colors ${styles[variant]} ${className}`}
      {...props}
    />
  );
}

export function Panel({
  title,
  right,
  children,
  className = '',
  info,
  tour,
}: {
  title?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Ícone "i" ao lado do título, com a explicação da seção. */
  info?: HelpId;
  /** Alvo do tutorial da tela (data-tour). */
  tour?: string;
}) {
  return (
    <section className={`flex min-h-0 flex-col border border-line bg-panel ${className}`} data-tour={tour}>
      {title !== undefined && (
        <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2 font-mono text-[11px] font-bold tracking-[0.1em] uppercase">
          <span className="flex items-center gap-1.5">
            {title}
            {info && <InfoTip topic={info} />}
          </span>
          {right}
        </header>
      )}
      <div className="c2-scroll min-h-0 flex-1 overflow-auto">{children}</div>
    </section>
  );
}

export function Field({
  label,
  children,
  hint,
  info,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
  /** Ícone "i" ao lado do rótulo (fica fora do <label> para não entrar no nome do campo). */
  info?: HelpId;
}) {
  const field = (
    <label className="flex flex-col gap-1">
      <span className={`c2-label ${info ? 'pr-5' : ''}`}>{label}</span>
      {children}
      {hint && <span className="font-mono text-[10px] text-dim">{hint}</span>}
    </label>
  );
  if (!info) return field;
  return (
    <div className="relative">
      {field}
      <InfoTip topic={info} className="absolute top-0 right-0" />
    </div>
  );
}

export function Badge({
  children,
  tone = 'amber',
}: {
  children: ReactNode;
  tone?: 'amber' | 'friend' | 'hostile' | 'neutral' | 'dim';
}) {
  const tones = {
    amber: 'border-amber text-amber',
    friend: 'border-friend-line text-friend-2',
    hostile: 'border-hostile-line text-hostile-2',
    neutral: 'border-neutral-line text-neutral-2',
    dim: 'border-line-2 text-muted',
  };
  return (
    <span
      className={`inline-flex items-center border px-1.5 py-px font-mono text-[10px] tracking-[0.06em] uppercase ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function Alert({
  children,
  tone = 'hostile',
}: {
  children: ReactNode;
  tone?: 'hostile' | 'amber' | 'neutral';
}) {
  const tones = {
    hostile: 'border-hostile-line bg-[#2a1410] text-hostile-3',
    amber: 'border-amber-line bg-amber-bg text-amber-2',
    neutral: 'border-neutral-line bg-neutral-bg text-neutral-2',
  };
  return (
    <div role="alert" className={`border px-3 py-2 font-mono text-[11px] leading-relaxed ${tones[tone]}`}>
      {children}
    </div>
  );
}

export function Modal({
  open,
  onOpenChange,
  title,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70" />
        <Dialog.Content
          className={`fixed top-1/2 left-1/2 z-50 max-h-[90vh] w-[94vw] -translate-x-1/2 -translate-y-1/2 overflow-auto border border-line-2 bg-panel p-0 shadow-2xl ${wide ? 'max-w-3xl' : 'max-w-lg'}`}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <Dialog.Title className="font-mono text-[12px] font-bold tracking-[0.1em] uppercase">
              {title}
            </Dialog.Title>
            <Dialog.Close className="font-mono text-muted hover:text-amber" aria-label="Fechar">
              ✕
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">{title}</Dialog.Description>
          <div className="p-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <div className="flex flex-col border-l border-line px-3 font-mono text-[11px] tracking-[0.06em]">
      <span className="text-dim uppercase">{label}</span>
      <span className={tone}>{value}</span>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="p-6 text-center font-mono text-[11px] text-dim">{children}</div>;
}

export function Loading() {
  return <Empty>CARREGANDO…</Empty>;
}

export function Bar({
  value,
  max,
  color = '#6FA8FF',
  dashed,
}: {
  value: number;
  max: number;
  color?: string;
  dashed?: boolean;
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <span className="block h-2 bg-[#16202B]">
      <span
        className="block h-2"
        style={{
          width: `${pct}%`,
          background: dashed
            ? `repeating-linear-gradient(90deg, ${color} 0 4px, transparent 4px 6px)`
            : color,
        }}
      />
    </span>
  );
}
